import { pdf } from "@react-pdf/renderer";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { datadogRum } from "@datadog/browser-rum";
import { PDFDocument } from "pdf-lib";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import type { FolderObject } from "components/folder-picker/folder-picker.component";
import { isDigitalForm } from "components/form-templates-field/digital-forms.utils";
import { forgetFailedFontLoads } from "components/pdf-generation-v3/forget-failed-font-loads.util";
import type { FormSettings } from "components/pdf-generation-v3/form-print.utils";
import {
  PdfMeta,
  usePDFGenerationV3
} from "components/pdf-generation-v3/pdf-generation-v3.hook";
import type { MergeTagExtraData } from "components/word-processor-field/merge-tags/merge-tags-values/merge-tags-values";
import { setAiPromptDetails } from "components/word-processor-field/word-processor.slice";
import {
  CUSTOM_FORM_SIGNER_LICENSE_PICKER,
  HTML_FORMS_FEATURE,
  PRACTICE_FORM_DEFAULTS_FEATURE,
  VERTICAL_SOUTHWESTERN_LIMITATIONS
} from "constants/feature-flags";
import { PROMPT_TYPE_TEXT_TEMPLATE } from "constants/prompts";
import {
  selectCurrentClinic,
  selectCurrentUser
} from "features/auth/auth-slice";
import { PATIENT_HEADER_QUERY } from "features/patients/use-patient.hook";
import useClinicNotifications from "hooks/use-clinic-notifications";
import { useGetClinicQuery } from "services/clinics";
import {
  useGetBoxAccessTokenQuery,
  useUploadDocumentMutation
} from "services/e-documents";
import { useGetPatientQuery } from "services/patients";
import { useGetDocumentSignerQuery } from "services/staff";
import { useAutoCompleteTextTemplatesMutation } from "services/text-templates";
import type { FormTemplate } from "services/types/form-templates.type";
import type { Patient } from "services/types/patients.type";
import type { MinimalStaffMember } from "services/types/staff.type";
import {
  type ClaimedPrintTab,
  PRINT_TAB_BLOCKED_MESSAGE,
  claimPrintTab
} from "utils/common";
import { ensureHtmlLineBreaks } from "utils/format-compat";

import type { BlankMergeTag } from "./blank-merge-tags.util";
import MemoizedCustomPrintFormPdfRender from "./custom-print-form-pdf-render.component";
import { CUSTOM_FORM_OPEN_DEFAULTS } from "./form-open-defaults";
import {
  digitalFormNameOf,
  printedFormFileName
} from "./printed-form-file-name.util";
import {
  type SignerLicenseOption,
  getPrintedLicenseNumber,
  getSignerLicenseOptions
} from "./signer-license.util";

type AskAiParams = {
  id: string;
  type: number;
  name: string;
  prompt: string;
  fieldContent: string;
  lastResult?: string;
};

type CustomPrintFormPreviewProviderProps = {
  children?: React.ReactNode;
  aiFeatureEnabled?: boolean;
  providerSignatureEnabled?: boolean;
  enableHeaderDefaultValue?: boolean;
  enablePatientSignatureDefaultValue?: boolean;
  enableProviderSignatureDefaultValue?: boolean;
  practiceDefaultsContext?: "custom_form" | "purchase_agreement";
};

type CustomPrintFormPreviewContextType = {
  formContent: string;
  setFormContent: (formContent: string) => void;
  pdfMeta: PdfMeta | undefined;
  setPdfMeta: React.Dispatch<React.SetStateAction<PdfMeta | undefined>>;
  patientId: number | undefined;
  setPatientId: React.Dispatch<React.SetStateAction<number | undefined>>;
  showDialog: () => void;
  hideDialog: () => void;
  dialogOpened: boolean;
  handleAskAi: (params: AskAiParams) => Promise<void> | undefined;
  isTemplateCompleting: boolean;
  autocompleteTextTemplate: (text: string) => void;
  handlePrint: (content: string) => void;
  /** ENG-2499: the document as it stands, for a surface with no dialog on screen (Bulk Print). */
  renderPdfBlob: () => Promise<Blob>;
  patient?: Patient;
  metadata: CustomPrintFormMetadata;
  setMetadata: React.Dispatch<React.SetStateAction<CustomPrintFormMetadata>>;
  isReady: boolean;
  enableHeader: boolean;
  setEnableHeader: React.Dispatch<React.SetStateAction<boolean>>;
  setTemplate: React.Dispatch<
    React.SetStateAction<FormTemplate | null | undefined>
  >;
  enablePatientSignature: boolean;
  setEnablePatientSignature: React.Dispatch<React.SetStateAction<boolean>>;
  enableProviderSignature: boolean;
  setEnableProviderSignature: React.Dispatch<React.SetStateAction<boolean>>;
  //
  pdfFormBlobUrl: string | undefined;
  /** Last PDF generation failure, cleared when a new generation starts. */
  pdfGenerationError: unknown;
  showSignModal: boolean;
  setShowSignModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmitSignature: (
    files: File[],
    uploadToEdocs?: boolean
  ) => Promise<void> | void;
  signature: File | undefined;
  handleSelectBoxFolder: (
    selectedFolder: FolderObject | null,
    documentName: string
  ) => Promise<void>;
  /** The name the PDF is given: printed, emailed, and filed in E-Docs unless staff type another. */
  fileName: string;
  folderPickerOpen: boolean;
  setFolderPickerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isEdocsAvailable: boolean;
  boxFolderId?: string;
  boxAccessToken?: string | null;
  handleSaveToEdocs: () => void;
  isGeneratingPdfForEdocs: boolean;
  currentTab: string;
  setCurrentTab: React.Dispatch<React.SetStateAction<string>>;
  handleRegeneratePdf: () => void;
  handleShowSignModal: () => Promise<void>;
  aiFeatureEnabled?: boolean;
  providerSignatureEnabled?: boolean;
  setSignature: React.Dispatch<React.SetStateAction<File | undefined>>;
  wordProcessorFieldId: string;
  setWordProcessorFieldId: React.Dispatch<React.SetStateAction<string>>;
  nativePdfTablesEnabled: boolean;
  setNativePdfTablesEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  numberOfPages: number | null;
  recalculateNumberOfPagesWithContent: (content: string) => Promise<void>;
  footerSignerId: number | null;
  setFooterSignerId: React.Dispatch<React.SetStateAction<number | null>>;
  footerSignatureDataUrl: string | null;
  setFooterSignatureDataUrl: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  showFooterSignModal: boolean;
  setShowFooterSignModal: React.Dispatch<React.SetStateAction<boolean>>;
  /** The footer signer's state licenses. Empty with CUSTOM_FORM_SIGNER_LICENSE_PICKER off. */
  signerLicenseOptions: SignerLicenseOption[];
  /** The license number the footer prints for the current signer. */
  printedLicenseNumber: string | undefined;
  /** Prints another of the signer's licenses on this form only. */
  pickSignerLicense: (licenseNumber: string) => void;
  effectiveClinicId: number | undefined;
  /**
   * ENG-2277: the open digital Form's own name. Null for any other form, and always with
   * HTML_FORMS_FEATURE off.
   */
  digitalFormName: string | null;
  /**
   * ENG-2277: makes the PDF as Print does, from `content`, and hands it to `attach` named as E-Docs
   * saves it. A PDF that cannot be made is handed to nobody; the Preview tab opens on the failure.
   * Nor is one handed over once the dialog has been closed.
   */
  handleEmailPdf: (content: string, attach: (pdfFile: File) => void) => void;
  /** True from Email to Patient's click until its PDF is handed over, fails or is given up. */
  isMakingEmailPdf: boolean;
};

const CustomPrintFormPreviewContext =
  createContext<CustomPrintFormPreviewContextType | null>(null);

export type CustomPrintFormMetadata = {
  signedAt?: string | null;
  // With no signedAt: date each footer slot the day it is signed, as a digital Form does.
  datesSignaturesWhenSigned?: boolean;
  dateOfVisit?: string | null;
  staffMember?: MinimalStaffMember | null;
  clinicId?: number;
  // The visit the form is for: a telehealth visit signs with the license for the patient's state.
  appointmentId?: number;
  fileNamePostfix?: string;
  // ENG-2277: what tells the surface's own record apart in a file name, with no extension (the
  // purchase agreement dialog: its invoice, the agreement's date and the moment the name was made).
  // Only a digital Form's name reads it, after the form's own name. A surface that hands none over
  // has the date and time the form was opened in its place (printed-form-file-name.util).
  fileNameRecord?: string;
  additionalMergeTagsValues?: MergeTagExtraData;
  // ENG-2277 (HTML Forms): set only for a digital Form. Absent, the dialog behaves as before.
  formSettings?: FormSettings | null;
  // Tags with no value for this patient and surface: they print blank unless typed in.
  blankMergeTags?: BlankMergeTag[];
  // The patient's signature drawn on the surface itself (the purchase agreement dialog), handed over
  // as a digital Form opens: each open starts from what its surface hands over.
  signature?: File | null;
};

export function CustomPrintFormPreviewProvider({
  children,
  aiFeatureEnabled,
  providerSignatureEnabled,
  enableHeaderDefaultValue = CUSTOM_FORM_OPEN_DEFAULTS.clinic_header,
  enablePatientSignatureDefaultValue = CUSTOM_FORM_OPEN_DEFAULTS.patient_signature_in_footer,
  enableProviderSignatureDefaultValue = CUSTOM_FORM_OPEN_DEFAULTS.provider_signature_in_footer,
  practiceDefaultsContext = "custom_form"
}: CustomPrintFormPreviewProviderProps) {
  const flags = useFlags();
  const isPracticeFormDefaultsEnabled =
    flags[PRACTICE_FORM_DEFAULTS_FEATURE] ?? false;
  const verticalSouthwesternLimitationsEnabled =
    flags[VERTICAL_SOUTHWESTERN_LIMITATIONS];
  const htmlFormsEnabled = Boolean(flags[HTML_FORMS_FEATURE]);
  const isPdfNativeTablesEnabled = Boolean(
    useAppSelector(selectCurrentUser)?.native_pdf_tables_enabled ||
      verticalSouthwesternLimitationsEnabled
  );

  const dispatch = useAppDispatch();
  const currentClinic = useAppSelector(selectCurrentClinic);
  const uploadBlobRef = useRef<Blob | undefined>();
  // Claimed in the click handler, spent (or given back) by handleTriggerPrint one render
  // later — see claimPrintTab: the tab has to be taken while the click still counts.
  const printTabRef = useRef<ClaimedPrintTab | null>(null);
  // Who Email to Patient's PDF goes to: taken in the click and spent by handleTriggerEmailPdf one
  // render later, like the print tab above.
  const emailPdfRef = useRef<((pdfFile: File) => void) | null>(null);
  const [pdfFormBlobUrl, setPdfFormBlobUrl] = useState<string | undefined>();
  const [numberOfPages, setNumberOfPages] = useState<number | null>(null);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [pdfGenerationError, setPdfGenerationError] = useState<unknown>(null);

  const clinic_id = currentClinic?.id ? Number(currentClinic.id) : undefined;

  const [signature, setSignature] = useState<File | undefined>();
  const [footerSignerId, setFooterSignerId] = useState<number | null>(null);
  const [showSignModal, setShowSignModal] = useState(false);
  const [folderPickerOpen, setFolderPickerOpen] = useState(false);

  const { data: boxData } = useGetBoxAccessTokenQuery();

  const [metadata, setMetadata] = useState<CustomPrintFormMetadata>({});

  const effectiveClinicId = metadata.clinicId || clinic_id;

  // The form's clinic and visit pick which of the signer's state licenses prints.
  const { data: lastSigner, isFetching: staffMemberFetching } =
    useGetDocumentSignerQuery(
      {
        id: Number(metadata.staffMember?.id),
        licenseClinicId: effectiveClinicId,
        licenseAppointmentId: metadata.appointmentId
      },
      { skip: !metadata.staffMember?.id }
    );
  // `skip` keeps the last response, so a form with no signer would print the previous form's.
  const staffMember = metadata.staffMember?.id ? lastSigner : undefined;

  const { data: footerSignerStaffMember, isFetching: footerSignerFetching } =
    useGetDocumentSignerQuery(
      {
        id: Number(footerSignerId),
        licenseClinicId: effectiveClinicId,
        licenseAppointmentId: metadata.appointmentId
      },
      { skip: !footerSignerId }
    );

  const [initialized, setInitialized] = useState(false);
  const [patientId, setPatientId] = useState<number | undefined>();
  const lastPracticeIdRef = useRef<number | undefined>(undefined);

  const [formContent, setFormContent] = useState("");
  const [triggerPrint, setTriggerPrint] = useState(false);
  const [triggerGeneratePdf, setTriggerGeneratePdf] = useState(false);
  const [triggerSaveToEdocs, setTriggerSaveToEdocs] = useState(false);
  const [triggerEmailPdf, setTriggerEmailPdf] = useState(false);
  const [isMakingEmailPdf, setIsMakingEmailPdf] = useState(false);
  const [isGeneratingPdfForEdocs, setIsGeneratingPdfForEdocs] = useState(false);
  const [nativePdfTablesEnabled, setNativePdfTablesEnabled] = useState(
    isPdfNativeTablesEnabled
  );
  const [enableHeader, setEnableHeader] = useState(enableHeaderDefaultValue);
  const [enablePatientSignature, setEnablePatientSignature] = useState(
    enablePatientSignatureDefaultValue
  );
  const [enableProviderSignature, setEnableProviderSignature] = useState(
    providerSignatureEnabled ? enableProviderSignatureDefaultValue : false
  );

  const [footerSignatureDataUrl, setFooterSignatureDataUrl] = useState<
    string | null
  >(null);
  const [showFooterSignModal, setShowFooterSignModal] = useState(false);

  const [wordProcessorFieldId, setWordProcessorFieldId] = useState("");
  const [currentTab, setCurrentTab] = useState("form");

  const [template, setTemplate] = useState<FormTemplate | null | undefined>(
    undefined
  );

  const [showPrintableFormDialog, setShowPrintableFormDialog] = useState(false);
  // ENG-2277: when the dialog was last opened. Only a digital Form's file name reads it.
  const [openedAt, setOpenedAt] = useState<Date | null>(null);

  const { pdfMeta, setPdfMeta } = usePDFGenerationV3();

  const [autocompleteTemplate, { isLoading: isTemplateCompleting }] =
    useAutoCompleteTextTemplatesMutation();

  const { clearCompletionChunks } = useClinicNotifications();

  const [uploadDocument, { isLoading: isUploadingToEDocs }] =
    useUploadDocumentMutation();

  useEffect(() => {
    setNativePdfTablesEnabled(isPdfNativeTablesEnabled);
  }, [isPdfNativeTablesEnabled]);

  const { data: patient, isFetching: isPatientFetching } = useGetPatientQuery(
    { patientId: Number(patientId), query: PATIENT_HEADER_QUERY },
    {
      skip: !patientId || patientId === -1 || !template
    }
  );

  // Latch the Box access token to its first truthy value while the folder
  // picker is open, then reset it on close. The picker refreshes its own
  // token internally right before calling Box's show(), and that refresh
  // updates the shared getBoxAccessToken cache. Reading the live cache value
  // here would feed that churn back into the picker's mount gate and token
  // prop, producing a token-refetch/remount loop, so we hold a stable token
  // for as long as the dialog stays open.
  const [stableBoxToken, setStableBoxToken] = useState<string | null>(null);

  useEffect(() => {
    if (!folderPickerOpen) {
      setStableBoxToken(null);
      return;
    }
    setStableBoxToken((prev) => prev ?? boxData?.box_access_token ?? null);
  }, [folderPickerOpen, boxData?.box_access_token]);

  const isEdocsAvailable = Boolean(patient?.box_folder_id && stableBoxToken);

  const { data: printClinic, isFetching: isClinicFetching } = useGetClinicQuery(
    { clinicId: Number(metadata.clinicId || clinic_id) },
    {
      skip: !(metadata.clinicId || clinic_id) || !template,
      refetchOnMountOrArgChange: true
    }
  );

  const handleAskAi = useCallback(
    (params: AskAiParams) => {
      const { id, type, name, prompt, fieldContent, lastResult } = params;
      clearCompletionChunks();
      dispatch(
        setAiPromptDetails({
          id,
          prompt,
          result: lastResult,
          dialogOpen: true,
          isBusy: true
        })
      );

      return autocompleteTemplate({
        name,
        text: fieldContent,
        user_prompt: prompt,
        type,
        clinic_id,
        format: "html"
      }).then(({ data }: any) => {
        clearCompletionChunks();
        dispatch(
          setAiPromptDetails({
            id,
            prompt,
            result: ensureHtmlLineBreaks(data),
            dialogOpen: true,
            isBusy: false
          })
        );
      });
    },
    [autocompleteTemplate, clearCompletionChunks, clinic_id, dispatch]
  );

  const digitalFormName = digitalFormNameOf(template, htmlFormsEnabled);
  const fileName = printedFormFileName({
    patient,
    digitalFormName,
    surface: metadata,
    openedAt
  });

  const autocompleteTextTemplate = useCallback(
    (text: string) => {
      setCurrentTab("form");
      clearCompletionChunks();

      dispatch(
        setAiPromptDetails({
          id: wordProcessorFieldId,
          prompt: "",
          dialogOpen: true,
          isBusy: true
        })
      );
      autocompleteTemplate({
        name: "Appointment printable form",
        text,
        type: PROMPT_TYPE_TEXT_TEMPLATE,
        clinic_id,
        format: "html"
      }).then(({ data }: any) => {
        dispatch(
          setAiPromptDetails({
            id: wordProcessorFieldId,
            result: ensureHtmlLineBreaks(data),
            isBusy: false
          })
        );
      });
    },
    [
      wordProcessorFieldId,
      autocompleteTemplate,
      clearCompletionChunks,
      clinic_id,
      dispatch
    ]
  );

  const handleHidePrintableFormDialog = useCallback(() => {
    setShowPrintableFormDialog(false);
    setTriggerPrint(false);
    // An email whose PDF is still being made is given up: it would open over whatever is on
    // screen by then.
    emailPdfRef.current = null;
    setTriggerEmailPdf(false);
    setIsMakingEmailPdf(false);
    setCurrentTab("form");
    setFooterSignerId(null);
    setFooterSignatureDataUrl(null);
  }, []);

  // Counts the dialog's opens, so a digital Form's defaults apply afresh each time (see below).
  const [opens, setOpens] = useState(0);
  const handleShowPrintableFormDialog = useCallback(() => {
    setShowPrintableFormDialog(true);
    setTriggerPrint(false);
    setOpens((count) => count + 1);
    setOpenedAt(new Date());
  }, []);

  const staffMemberSignature = staffMember?.signature;

  const effectiveStaffMember =
    footerSignerId && footerSignerStaffMember
      ? (footerSignerStaffMember as MinimalStaffMember)
      : (staffMember as MinimalStaffMember);

  // A license pick belongs to one signer in one open of the dialog: another signer, or the next
  // form, starts again from the license resolved for its clinic.
  const signerLicensePickerEnabled = Boolean(
    flags[CUSTOM_FORM_SIGNER_LICENSE_PICKER]
  );
  const effectiveSigner =
    footerSignerId && footerSignerStaffMember
      ? footerSignerStaffMember
      : staffMember;
  const effectiveSignerId = effectiveSigner?.id;
  const [licensePick, setLicensePick] = useState<{
    signerId: number;
    open: number;
    licenseNumber: string;
  } | null>(null);
  // Choosing a footer signer, even switching back to the one already picked for, starts again
  // from the resolved license. This is the setter the dialog gets.
  const chooseFooterSigner = useCallback<
    React.Dispatch<React.SetStateAction<number | null>>
  >((value) => {
    setLicensePick(null);
    setFooterSignerId(value);
  }, []);
  const pickedLicenseNumber =
    signerLicensePickerEnabled &&
    licensePick?.signerId === effectiveSignerId &&
    licensePick?.open === opens
      ? licensePick?.licenseNumber
      : undefined;
  const signerLicenseOptions = useMemo(
    () =>
      signerLicensePickerEnabled
        ? getSignerLicenseOptions(
            effectiveSigner,
            getPrintedLicenseNumber(effectiveSigner, undefined)
          )
        : [],
    [signerLicensePickerEnabled, effectiveSigner]
  );
  const printedLicenseNumber = getPrintedLicenseNumber(
    effectiveSigner,
    pickedLicenseNumber
  );
  const pickSignerLicense = useCallback(
    (licenseNumber: string) => {
      if (!effectiveSignerId) return;
      setLicensePick({
        signerId: effectiveSignerId,
        open: opens,
        licenseNumber
      });
    },
    [effectiveSignerId, opens]
  );
  const effectiveSignerSignature =
    footerSignatureDataUrl ??
    (footerSignerId && footerSignerStaffMember
      ? footerSignerStaffMember.signature
      : staffMemberSignature);

  // ENG-2277: the footer's signature slots print their image only with a signed date, and only the
  // purchase agreement dialog and Appointment Forms pass one. A digital Form signed in this dialog
  // is dated the day it was signed, slot by slot: the patient's by Sign, the provider's by the
  // footer signer's Sign now. One date for both would print the provider's saved signature the
  // moment the patient signed. A surface's own date still wins, for the whole footer. Any other
  // custom form, and every form with HTML_FORMS_FEATURE off, prints as it did before the feature,
  // unless its surface asks for this (ENG-2646: Form Bundles, which has no date with no appointment).
  const [patientSignedHereAt, setPatientSignedHereAt] = useState<string | null>(
    null
  );
  const [providerSignedHereAt, setProviderSignedHereAt] = useState<
    string | null
  >(null);
  // Set while rendering, not in an effect: a commit with a signature and no date would start a PDF
  // that prints it unsigned, and that PDF can finish after the dated one and replace it on screen.
  if (Boolean(signature) !== Boolean(patientSignedHereAt)) {
    setPatientSignedHereAt(signature ? new Date().toISOString() : null);
  }
  if (Boolean(footerSignatureDataUrl) !== Boolean(providerSignedHereAt)) {
    setProviderSignedHereAt(
      footerSignatureDataUrl ? new Date().toISOString() : null
    );
  }
  const datesFooterSlotsHere =
    metadata.signedAt == null &&
    (metadata.datesSignaturesWhenSigned ||
      (htmlFormsEnabled && isDigitalForm(template)));
  // A surface that asks for dates when signed prints a date only beside a signature that prints.
  const signedHereAt = metadata.datesSignaturesWhenSigned
    ? ((enablePatientSignature ? patientSignedHereAt : null) ??
      (enableProviderSignature ? providerSignedHereAt : null))
    : (patientSignedHereAt ?? providerSignedHereAt);
  const footerSignedAt =
    metadata.signedAt ??
    (datesFooterSlotsHere ? signedHereAt : null) ??
    undefined;
  const patientSignedAt = datesFooterSlotsHere
    ? patientSignedHereAt
    : undefined;
  const providerSignedAt = datesFooterSlotsHere
    ? providerSignedHereAt
    : undefined;

  const updateNumberOfPages = useCallback(async (blob: Blob) => {
    const pdfDoc = await PDFDocument.load(await blob.arrayBuffer());
    const pageCount = pdfDoc.getPageCount();
    setNumberOfPages(pageCount);
  }, []);

  const pdfDocument = useMemo(() => {
    return (
      <MemoizedCustomPrintFormPdfRender
        flags={flags}
        content={formContent}
        isPdfNativeTablesEnabled={
          nativePdfTablesEnabled || verticalSouthwesternLimitationsEnabled
        }
        staffMemberSignature={effectiveSignerSignature}
        patient={patient}
        clinic={printClinic}
        fileName={fileName}
        enableHeader={enableHeader || verticalSouthwesternLimitationsEnabled}
        enablePatientSignature={enablePatientSignature}
        enableProviderSignature={enableProviderSignature}
        signature={signature}
        signedAt={footerSignedAt}
        patientSignedAt={patientSignedAt}
        providerSignedAt={providerSignedAt}
        dateOfVisit={metadata.dateOfVisit}
        staffMember={effectiveStaffMember}
        signerLicenseNumber={pickedLicenseNumber}
        formSettings={metadata.formSettings}
      />
    );
  }, [
    metadata.formSettings,
    pickedLicenseNumber,
    formContent,
    effectiveSignerSignature,
    patient,
    printClinic,
    fileName,
    enableHeader,
    enablePatientSignature,
    enableProviderSignature,
    signature,
    footerSignedAt,
    patientSignedAt,
    providerSignedAt,
    metadata.dateOfVisit,
    effectiveStaffMember,
    nativePdfTablesEnabled,
    flags,
    verticalSouthwesternLimitationsEnabled
  ]);

  const recalculateNumberOfPagesWithContent = useCallback(
    async (content: string) => {
      const tempPdfDocument = (
        <MemoizedCustomPrintFormPdfRender
          flags={flags}
          content={content}
          isPdfNativeTablesEnabled={
            nativePdfTablesEnabled || verticalSouthwesternLimitationsEnabled
          }
          staffMemberSignature={effectiveSignerSignature}
          patient={patient}
          clinic={printClinic}
          fileName={fileName}
          enableHeader={enableHeader || verticalSouthwesternLimitationsEnabled}
          enablePatientSignature={enablePatientSignature}
          enableProviderSignature={enableProviderSignature}
          signature={signature}
          signedAt={footerSignedAt}
          patientSignedAt={patientSignedAt}
          providerSignedAt={providerSignedAt}
          dateOfVisit={metadata.dateOfVisit}
          staffMember={effectiveStaffMember}
          signerLicenseNumber={pickedLicenseNumber}
          formSettings={metadata.formSettings}
        />
      );
      const blob = await pdf(tempPdfDocument).toBlob();
      updateNumberOfPages(blob);
    },
    [
      flags,
      pickedLicenseNumber,
      nativePdfTablesEnabled,
      effectiveSignerSignature,
      patient,
      printClinic,
      fileName,
      enableHeader,
      enablePatientSignature,
      enableProviderSignature,
      signature,
      footerSignedAt,
      patientSignedAt,
      providerSignedAt,
      metadata.dateOfVisit,
      metadata.formSettings,
      effectiveStaffMember,
      updateNumberOfPages,
      verticalSouthwesternLimitationsEnabled
    ]
  );

  const handlePrint = useCallback(
    async (content: string) => {
      // Synchronously, before anything defers: printing is two renders away, and by then
      // the click's user activation is gone and the browser refuses to open a tab.
      printTabRef.current?.close();
      printTabRef.current = claimPrintTab("the form");

      if (!printTabRef.current) {
        dispatch(setCustomMessage(PRINT_TAB_BLOCKED_MESSAGE));
        dispatch(setOpen(true));
        return;
      }

      setFormContent(content);
      setTriggerPrint(true);
    },
    [dispatch]
  );

  const handleRegeneratePdf = useCallback(async () => {
    setTriggerGeneratePdf(true);
  }, []);

  const handleTriggerRegeneratePdf = useCallback(async () => {
    setIsBusy(true);
    setPdfGenerationError(null);

    try {
      const blob = await pdf(pdfDocument).toBlob();
      uploadBlobRef.current = blob;

      updateNumberOfPages(blob);

      if (pdfFormBlobUrl) {
        URL.revokeObjectURL(pdfFormBlobUrl);
      }
      const url = URL.createObjectURL(blob);
      setPdfFormBlobUrl(url);

      return url;
    } catch (error) {
      // Without this the failure was invisible: no log, and the flags below never reset, so
      // isBusy pinned the UI disabled while triggerGeneratePdf stayed armed and re-fired on
      // every identity change. Rethrown so each caller keeps deciding for itself.
      datadogRum.addError(error, {
        context: { source: "custom_print_form_pdf_generation" }
      });
      // Exposed rather than clearing pdfFormBlobUrl: consumers that only read the url keep
      // their current behaviour, while a consumer that reads this can refuse to present a
      // document that no longer matches what the user selected.
      setPdfGenerationError(error);
      // Otherwise react-pdf replays a failed font fetch forever and Retry can never succeed.
      forgetFailedFontLoads();
      throw error;
    } finally {
      setTimeout(() => {
        setIsBusy(false);
      }, 1);
      setTriggerGeneratePdf(false);
    }
  }, [pdfDocument, pdfFormBlobUrl, updateNumberOfPages]);

  // Moves none of the dialog's state (its preview, page count, busy flag), as there is no dialog.
  const renderPdfBlob = useCallback(async () => {
    try {
      return await pdf(pdfDocument).toBlob();
    } catch (error) {
      datadogRum.addError(error, {
        context: { source: "custom_print_form_pdf_generation" }
      });
      // Otherwise react-pdf replays a failed font fetch, and every later form in the run fails too.
      forgetFailedFontLoads();
      throw error;
    }
  }, [pdfDocument]);

  useEffect(() => {
    if (triggerGeneratePdf) {
      // Already logged inside; caught so a failure is not an unhandled rejection.
      handleTriggerRegeneratePdf().catch(() => {});
    }
  }, [handleTriggerRegeneratePdf, triggerGeneratePdf]);

  const handleTriggerPrint = useCallback(async () => {
    setTriggerPrint(false);
    const printTab = printTabRef.current;
    printTabRef.current = null;

    try {
      const url = await handleTriggerRegeneratePdf();
      void printTab?.print(String(url));
    } catch {
      // Nothing to print, so give the claimed tab back rather than leaving an empty one
      // parked in the user's browser.
      printTab?.close();
      // Staying silent here is what made the click look like it did nothing at all.
      // handleTriggerRegeneratePdf has already recorded pdfGenerationError, so send the
      // user to the tab that renders it — with a Retry — instead of back to the editor.
      setCurrentTab("pdf");
    }
  }, [handleTriggerRegeneratePdf]);

  useEffect(() => {
    if (triggerPrint) {
      handleTriggerPrint();
    }
  }, [handleTriggerPrint, triggerPrint]);

  // ENG-2277: Email to Patient for a digital Form. Its PDF is made the way Print's is, from the same
  // document one render after the edited text is taken, so the patient is sent what the Preview tab
  // shows, what prints and what E-Docs saves, under the name E-Docs saves it by.
  const handleEmailPdf = useCallback(
    (content: string, attach: (pdfFile: File) => void) => {
      emailPdfRef.current = attach;
      setFormContent(content);
      setIsMakingEmailPdf(true);
      setTriggerEmailPdf(true);
    },
    []
  );

  const handleTriggerEmailPdf = useCallback(async () => {
    setTriggerEmailPdf(false);
    const attach = emailPdfRef.current;
    if (!attach) return;

    try {
      await handleTriggerRegeneratePdf();
    } catch {
      // As for Print: nothing to send, so show the failure and its Retry rather than open an
      // email with no form in it.
      if (emailPdfRef.current === attach) {
        emailPdfRef.current = null;
        setIsMakingEmailPdf(false);
        setCurrentTab("pdf");
      }
      return;
    }
    // Closed while the PDF was being made: the request is no longer the one waiting.
    if (emailPdfRef.current !== attach) return;
    emailPdfRef.current = null;
    setIsMakingEmailPdf(false);
    // The PDF just made: the one the Preview tab now shows.
    const blob = uploadBlobRef.current;
    if (blob) {
      attach(new File([blob], fileName, { type: "application/pdf" }));
    }
  }, [fileName, handleTriggerRegeneratePdf]);

  useEffect(() => {
    if (triggerEmailPdf) {
      handleTriggerEmailPdf();
    }
  }, [handleTriggerEmailPdf, triggerEmailPdf]);

  const handleSaveToEdocs = useCallback(() => {
    // Use trigger pattern to ensure PDF is generated in next render cycle
    // after all state updates (including signature) have been applied
    setIsGeneratingPdfForEdocs(true);
    setTriggerSaveToEdocs(true);
  }, []);

  const handleTriggerSaveToEdocs = useCallback(() => {
    setTriggerSaveToEdocs(false);
    // Use setTimeout to let the UI update and show loading state before heavy PDF generation
    setTimeout(async () => {
      try {
        // By this point, pdfDocument memo has been recalculated with the latest signature
        const blob = await pdf(pdfDocument).toBlob();
        uploadBlobRef.current = blob;
        setFolderPickerOpen(true);
      } finally {
        setIsGeneratingPdfForEdocs(false);
      }
    }, 0);
  }, [pdfDocument]);

  useEffect(() => {
    if (triggerSaveToEdocs) {
      handleTriggerSaveToEdocs();
    }
  }, [handleTriggerSaveToEdocs, triggerSaveToEdocs]);

  const handleSubmitSignature = useCallback(
    (files: File[], uploadToEdocs?: boolean) => {
      const signature = files?.at(0);
      setSignature(signature);

      if (uploadToEdocs) {
        // Use trigger pattern - PDF will be generated in next render cycle
        // after signature state has been applied to the pdfDocument memo
        setIsGeneratingPdfForEdocs(true);
        setTriggerSaveToEdocs(true);
      } else {
        // Regenerate PDF preview to show the signature
        handleRegeneratePdf();
      }
    },
    [handleRegeneratePdf]
  );

  const handleSelectBoxFolder = useCallback(
    async (selectedFolder: FolderObject | null, documentName: string) => {
      const formData = new FormData();

      const blob = uploadBlobRef.current;

      if (patient && blob) {
        formData.append("document", blob, documentName);
        formData.append(
          "destination_folder_id",
          String(selectedFolder?.id || patient.box_folder_id)
        );

        uploadDocument({
          id: patient.id,
          type: "patients" as const,
          formData: formData
        }).then((response) => {
          if (response && "error" in response) {
            dispatch(
              setCustomMessage(<ErrorSnackbarContent response={response} />)
            );
            dispatch(setOpen(true));
          } else {
            dispatch(
              setCustomMessage("PDF File Was Uploaded to Selected Folder")
            );
            dispatch(setOpen(true));
          }
        });
      }
    },
    [dispatch, patient, uploadDocument]
  );

  const handleShowSignModal = useCallback(async () => {
    try {
      await handleTriggerRegeneratePdf();
      setShowSignModal(true);
    } catch {
      // Keep the pre-existing behaviour of not opening the signing dialog when the document
      // could not be regenerated — signing a stale render would be worse.
    }
  }, [handleTriggerRegeneratePdf]);

  const staffMemberLoading = metadata.staffMember?.id
    ? staffMemberFetching
    : false;
  const footerSignerLoading = footerSignerId ? footerSignerFetching : false;

  const isInitialized = Boolean(
    !staffMemberLoading &&
      patient &&
      printClinic &&
      template &&
      !isPatientFetching &&
      !isClinicFetching
  );

  const isReady = Boolean(
    isInitialized && !isUploadingToEDocs && !footerSignerLoading
  );

  const digitalFormOpens = isDigitalForm(template) ? opens : 0;
  const practice = printClinic?.practice;
  const ctx = !isPracticeFormDefaultsEnabled
    ? undefined
    : practiceDefaultsContext === "purchase_agreement"
      ? practice?.form_rendering_defaults?.purchase_agreement
      : practice?.form_rendering_defaults?.custom_form;
  const practiceFields = {
    clinic_header: ctx?.clinic_header,
    provider_sig: ctx?.provider_signature_in_footer,
    patient_sig: ctx?.patient_signature_in_footer,
    etr: ctx?.enhanced_table_rendering
  };

  // ENG-2639: what the defaults below were last applied for. A refetch of the patient, clinic or
  // signer already held takes isInitialized false and back with all of this unchanged, and is not a
  // new form: the first Email to Patient causes one, through EmailForm's clinic query. The practice's
  // defaults count by value, as a form can open on a cached clinic older than its refetch.
  const defaultsAreFor = JSON.stringify([
    digitalFormOpens,
    isDigitalForm(template) ? null : practiceFields,
    effectiveClinicId,
    enableHeaderDefaultValue,
    enablePatientSignatureDefaultValue,
    enableProviderSignatureDefaultValue,
    isPracticeFormDefaultsEnabled,
    metadata.appointmentId,
    metadata.staffMember?.id,
    patientId,
    practiceDefaultsContext,
    printClinic?.practice.id,
    providerSignatureEnabled,
    template?.id,
    verticalSouthwesternLimitationsEnabled
  ]);
  const defaultsAppliedForRef = useRef<string | null>(null);
  useEffect(() => {
    // A surface that clears its template on close gets the defaults again when it reopens.
    if (!template) defaultsAppliedForRef.current = null;
    if (!isInitialized || defaultsAppliedForRef.current === defaultsAreFor) {
      return;
    }
    defaultsAppliedForRef.current = defaultsAreFor;

    const practiceId = printClinic?.practice.id;
    // On first mount lastPracticeIdRef.current is undefined, so practiceChanged is always
    // false — the empty initial state comes from useState(""), not this effect.
    const practiceChanged =
      lastPracticeIdRef.current !== undefined &&
      lastPracticeIdRef.current !== practiceId;
    lastPracticeIdRef.current = practiceId;

    if (practiceChanged) {
      setFormContent("");
    }
    const getDefault = (
      practiceValue: boolean | null | undefined,
      fallback: boolean
    ) => (practiceValue != null ? practiceValue : fallback);

    // ENG-2277: a digital Form usually carries its own header and signature boxes, so it opens as it
    // was drawn, with only what Suno adds that the form itself asks for (its print_defaults, set in
    // the Digital Form editor). Each can still be turned on or off here.
    const digitalForm = isDigitalForm(template);
    const formAsks = metadata.formSettings?.print_defaults ?? {};

    setEnableHeader(
      digitalForm
        ? Boolean(formAsks.clinic_header)
        : getDefault(practiceFields.clinic_header, enableHeaderDefaultValue)
    );
    setEnablePatientSignature(
      digitalForm
        ? Boolean(formAsks.patient_signature_in_footer)
        : getDefault(
            practiceFields.patient_sig,
            enablePatientSignatureDefaultValue
          )
    );
    setEnableProviderSignature(
      providerSignatureEnabled
        ? digitalForm
          ? Boolean(formAsks.provider_signature_in_footer)
          : getDefault(
              practiceFields.provider_sig,
              enableProviderSignatureDefaultValue
            )
        : false
    );
    // Vertical Southwestern practices have the ETR toggle hidden and native
    // tables forced on — a practice default must not silently override a
    // rendering mode the user cannot see or change per-form.
    if (practiceFields.etr != null && !verticalSouthwesternLimitationsEnabled) {
      setNativePdfTablesEnabled(practiceFields.etr);
    }
    // A digital Form starts from the signature its surface hands over as it opens, if any: the
    // purchase agreement dialog only pushes its own when it changes, which this reset would lose.
    setSignature(digitalForm ? (metadata.signature ?? undefined) : undefined);
    setFooterSignerId(null);
    setFooterSignatureDataUrl(null);
    setInitialized(true);
    // Keyed on practice.id intentionally: re-applying form defaults on every
    // practice object identity change would clobber the user's manual toggles.
    // ENG-2277: a digital Form's are re-applied on every open, as it says what it prints with (and may
    // have been edited since), so no open inherits the last one's switches or the signature drawn in
    // it. Other forms keep their switches between opens, as before.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultsAreFor, isInitialized]);

  // ENG-2277: a digital Form holds the patient's details, and none of them may reach the AI
  // service, so Enhance Form and Ask AI are off for it whatever the surface's provider allows.
  const digitalFormOpen = isDigitalForm(template);

  const value = useMemo(() => {
    return {
      formContent,
      setFormContent,
      pdfMeta,
      setPdfMeta,
      patientId,
      setPatientId,
      hideDialog: handleHidePrintableFormDialog,
      showDialog: handleShowPrintableFormDialog,
      dialogOpened: showPrintableFormDialog,
      handleAskAi,
      isTemplateCompleting,
      autocompleteTextTemplate,
      handlePrint,
      patient,
      metadata,
      setMetadata,
      isReady: isReady && !isBusy,
      enableHeader,
      setEnableHeader,
      setTemplate,
      enablePatientSignature,
      setEnablePatientSignature,
      enableProviderSignature,
      setEnableProviderSignature,
      pdfFormBlobUrl,
      pdfGenerationError,
      showSignModal,
      setShowSignModal,
      handleSubmitSignature,
      signature,
      handleSelectBoxFolder,
      fileName,
      folderPickerOpen,
      setFolderPickerOpen,
      isEdocsAvailable,
      boxFolderId: patient?.box_folder_id,
      boxAccessToken: stableBoxToken,
      handleSaveToEdocs,
      isGeneratingPdfForEdocs,
      currentTab,
      setCurrentTab,
      handleShowSignModal,
      handleRegeneratePdf,
      initialized,
      renderPdfBlob,
      aiFeatureEnabled: aiFeatureEnabled && !digitalFormOpen,
      providerSignatureEnabled,
      setSignature,
      wordProcessorFieldId,
      setWordProcessorFieldId,
      nativePdfTablesEnabled,
      setNativePdfTablesEnabled,
      numberOfPages,
      recalculateNumberOfPagesWithContent,
      footerSignerId,
      setFooterSignerId: chooseFooterSigner,
      footerSignatureDataUrl,
      setFooterSignatureDataUrl,
      showFooterSignModal,
      setShowFooterSignModal,
      signerLicenseOptions,
      printedLicenseNumber,
      pickSignerLicense,
      effectiveClinicId,
      digitalFormName,
      handleEmailPdf,
      isMakingEmailPdf
    };
  }, [
    formContent,
    pdfMeta,
    setPdfMeta,
    patientId,
    handleHidePrintableFormDialog,
    handleShowPrintableFormDialog,
    showPrintableFormDialog,
    wordProcessorFieldId,
    handleAskAi,
    isTemplateCompleting,
    autocompleteTextTemplate,
    handlePrint,
    patient,
    metadata,
    isReady,
    isBusy,
    enableHeader,
    enablePatientSignature,
    enableProviderSignature,
    pdfFormBlobUrl,
    pdfGenerationError,
    showSignModal,
    handleSubmitSignature,
    signature,
    handleSelectBoxFolder,
    fileName,
    folderPickerOpen,
    isEdocsAvailable,
    stableBoxToken,
    handleSaveToEdocs,
    isGeneratingPdfForEdocs,
    currentTab,
    handleShowSignModal,
    handleRegeneratePdf,
    initialized,
    renderPdfBlob,
    aiFeatureEnabled,
    digitalFormOpen,
    providerSignatureEnabled,
    nativePdfTablesEnabled,
    setNativePdfTablesEnabled,
    numberOfPages,
    recalculateNumberOfPagesWithContent,
    footerSignerId,
    chooseFooterSigner,
    footerSignatureDataUrl,
    showFooterSignModal,
    signerLicenseOptions,
    printedLicenseNumber,
    pickSignerLicense,
    effectiveClinicId,
    digitalFormName,
    handleEmailPdf,
    isMakingEmailPdf
  ]);

  return (
    <CustomPrintFormPreviewContext.Provider value={value}>
      {children}
    </CustomPrintFormPreviewContext.Provider>
  );
}

/**
 * ENG-2277: the print dialog's context, or null outside a CustomPrintFormPreviewProvider. For code
 * that must run on surfaces that only mount the provider with HTML_FORMS_FEATURE on.
 */
export function useOptionalCustomPrintFormPreview() {
  return useContext(CustomPrintFormPreviewContext);
}

export default function useCustomPrintFormPreview() {
  const context = useContext(CustomPrintFormPreviewContext);
  if (!context) {
    throw new Error(
      `useCustomPrintFormPreview must be used within an CustomPrintFormPreviewProvider`
    );
  }

  return context;
}
